/**
 * Parse weight in grams from a product's size field.
 * Handles formats like: "3.5g", "7g", "0.5g", "1g", "12 x 0.6g"
 * Returns 0 for non-weight sizes (e.g., "10mg/ea", "30ml", "5 Pack")
 */
export function parseWeight(size: string | number | null | undefined): number {
  if (!size) return 0
  if (typeof size === 'number') return size
  
  const sizeStr = String(size)

  // Handle "12 x 0.6g" format (multiply quantity by unit weight)
  const multiMatch = sizeStr.match(/(\d+)\s*x\s*([\d.]+)\s*g/i)
  if (multiMatch) {
    const count = parseFloat(multiMatch[1])
    const unitWeight = parseFloat(multiMatch[2])
    return count * unitWeight
  }

  // Handle standard "3.5g" format
  const standardMatch = sizeStr.match(/([\d.]+)\s*g$/i)
  if (standardMatch) {
    return parseFloat(standardMatch[1])
  }

  // Non-weight sizes (ml, mg, pack, caps, etc.) don't count toward limit
  return 0
}

/**
 * Calculate total weight of cart items in grams
 */
export function calculateCartWeight(
  cart: { product: { size?: string; mj_grams?: number | null }; quantity: number }[]
): number {
  return cart.reduce((sum, item) => {
    // First try mj_grams from API, then parse size string
    const weight = item.product.mj_grams || parseWeight(item.product.size)
    return sum + weight * item.quantity
  }, 0)
}

/**
 * Check if adding a product would exceed the purchase limit
 */
export function canAddToCart(
  productSize: string | number | null | undefined,
  productMjGrams: number | null | undefined,
  quantity: number,
  currentCartWeight: number,
  customerCurrentPurchase: number,
  purchaseLimit: number
): { allowed: boolean; maxQuantity: number; reason?: string } {
  const productWeight = productMjGrams || parseWeight(productSize)

  // Non-weight items (edibles, oils, etc.) don't count toward limit
  if (productWeight === 0) {
    return { allowed: true, maxQuantity: Infinity }
  }

  const totalUsedToday = customerCurrentPurchase + currentCartWeight
  const remainingLimit = purchaseLimit - totalUsedToday
  const requestedWeight = productWeight * quantity

  if (requestedWeight <= remainingLimit) {
    return { allowed: true, maxQuantity: Math.floor(remainingLimit / productWeight) }
  }

  const maxCanAdd = Math.floor(remainingLimit / productWeight)
  if (maxCanAdd <= 0) {
    return {
      allowed: false,
      maxQuantity: 0,
      reason: `Daily limit reached (${purchaseLimit}g)`,
    }
  }

  return {
    allowed: false,
    maxQuantity: maxCanAdd,
    reason: `Can only add ${maxCanAdd} more (${remainingLimit.toFixed(1)}g remaining)`,
  }
}

/**
 * Format weight for display
 */
export function formatWeight(grams: number): string {
  if (grams === 0) return '0g'
  if (grams < 1) return `${(grams * 1000).toFixed(0)}mg`
  return `${grams.toFixed(1)}g`
}
